import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box } from "@mui/material";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import { getAhaClassSetup } from "../../api/apiClient";
import { computeSetupSteps, setupChip } from "./ahaSetupSteps";
import { AHA_TOKENS as T } from "./ahaTokens";
import { isTestEnvironment } from "./ahaText";

const TONE = {
  lock: { color: T.muted, bg: "rgba(121,98,140,.1)", border: "rgba(121,98,140,.18)", Icon: LockOutlinedIcon },
  pause: { color: T.muted, bg: "rgba(121,98,140,.1)", border: "rgba(121,98,140,.18)", Icon: PauseCircleOutlineIcon },
  admin: { color: T.warn, bg: T.warnBg, border: "rgba(194,65,12,.2)", Icon: AdminPanelSettingsOutlinedIcon },
  ready: { color: T.limeInk, bg: "rgba(194,239,78,.45)", border: "rgba(110,140,20,.35)", Icon: CheckIcon },
  plain: { color: T.ink, bg: "rgba(121,98,140,.1)", border: "rgba(121,98,140,.18)", Icon: null },
};

const formatShort = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/** Per-student counts for the strip; students who aren't enrolled don't count. */
function countStudents(students = []) {
  const rows = students.filter((s) => !(s.blockers || []).includes("NOT_ENROLLED"));
  const attention = (s) => ["REJECTED", "UNKNOWN"].includes(s.issuanceState) || (s.blockers || []).includes("MISSING_EMAIL");
  return {
    total: rows.length,
    sent: rows.filter((s) => s.issuanceState === "ASSIGNED").length,
    willGet: rows.filter((s) => s.eligible && !s.issuanceState).length,
    attention: rows.filter((s) => s.issuanceState !== "ASSIGNED" && attention(s)).length,
    held: rows.filter((s) => !s.eligible && !s.issuanceState && !attention(s)).length,
  };
}

/**
 * The single AHA line above the roster: is this class ready to send AHA eCards, what is blocking it, and a link into
 * the Details checklist. Class-level blockers are said here once, never repeated per student.
 */
export default function AhaStatusStrip({ classId, aha, canOpenSetup = false, refreshKey }) {
  const [setup, setSetup] = useState(null);

  useEffect(() => {
    let active = true;
    // AHA must never break the roster: any failure (403, network) just hides the strip.
    Promise.resolve().then(() => getAhaClassSetup(classId))
      .then((data) => { if (active) setSetup(data); })
      .catch(() => { if (active) setSetup(null); });
    return () => { active = false; };
  }, [classId, refreshKey]);

  if (!setup || !setup.classIssuesAha) return null;

  const result = computeSetupSteps(setup);
  const chip = setupChip(result);
  const tone = TONE[chip.tone];
  const counts = countStudents(aha?.students);
  const admin = setup.centerAdmin?.name;
  const completed = setup.classStatus === "COMPLETED";
  const blocked = !completed && chip.tone === "admin";
  const inProgress = !completed && chip.tone === "plain";
  const missing = result.missingInstructors;

  let main;
  let sub = null;
  let linkText = "View AHA setup";
  if (completed) {
    main = `${counts.sent} of ${counts.total} AHA eCards sent.`;
    sub = counts.attention + counts.held > 0 ? ` ${counts.attention} needs attention, ${counts.held} held back.` : null;
    linkText = "View AHA setup (read-only)";
  } else if (blocked) {
    linkText = "Open AHA setup";
    if (!setup.centerReady) {
      main = "Your training center isn’t connected to the AHA yet.";
      sub = ` No AHA eCards can be sent until ${admin || "an admin"} sets it up.`;
    } else if (missing.length > 0) {
      main = `${missing.map((i) => i.name).join(", ")} ${missing.length > 1 ? "have" : "has"} no AHA instructor ID.`;
      sub = ` No AHA eCards can be sent until ${admin || "an admin"} adds ${missing.length > 1 ? "them" : "it"}.`;
    } else {
      main = "This class needs an admin’s help before AHA eCards can be sent.";
    }
  } else if (inProgress) {
    linkText = "Open AHA setup";
    main = "Finish the AHA setup before cards can be sent.";
  } else {
    main = `${counts.willGet} will get an AHA eCard`;
    const parts = [counts.held > 0 && `${counts.held} held back`, counts.attention > 0 && `${counts.attention} needs attention`].filter(Boolean);
    sub = parts.length ? ` · ${parts.join(" · ")}` : null;
  }

  const label = completed
    ? `Class completed${setup.completedAt ? ` ${formatShort(setup.completedAt)}` : ""}`
    : chip.label;
  const chipTone = completed ? TONE.lock : tone;

  return (
    <Box role="status" data-testid="aha-status-strip"
      sx={{ display: "flex", alignItems: "center", gap: "14px", p: "12px 16px", mb: 2, flexWrap: "wrap", fontSize: 14,
        borderRadius: "10px", border: `1px solid ${blocked ? "rgba(194,65,12,.25)" : T.hairlineCloud}`, bgcolor: blocked ? T.warnBg : "#fff" }}>
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px", height: 26, px: "10px", borderRadius: 999, fontSize: 12.5,
        fontWeight: 600, whiteSpace: "nowrap", color: chipTone.color, bgcolor: chipTone.bg, border: `1px solid ${chipTone.border}`, "& svg": { fontSize: 14 } }}>
        {chipTone.Icon && <chipTone.Icon />}{label}
      </Box>
      <Box component="span">
        <Box component="span" sx={{ fontWeight: 600 }}>{main}</Box>
        {sub && <Box component="span" sx={{ color: completed || blocked ? "inherit" : T.muted }}>{sub}</Box>}
      </Box>
      {blocked && !completed && (
        <Box component="span" sx={{ color: T.muted }}>{`${result.done} of 5 steps done`}</Box>
      )}
      {isTestEnvironment(setup.environment) && (
        <Box component="span" title="Test environment. Cards are not real."
          sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, px: "7px", py: "3px", borderRadius: "4px",
            bgcolor: T.ink, color: "#fff", letterSpacing: ".5px" }}>{setup.environment}</Box>
      )}
      <Box sx={{ flex: 1 }} />
      {canOpenSetup && (
        <Box component={RouterLink} to={`/course-management/${classId}/details`}
          sx={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: 14, fontWeight: 600, color: T.violet, textDecoration: "none",
            "&:hover": { color: T.ink } }}>
          {linkText}<ChevronRightIcon sx={{ fontSize: 16 }} />
        </Box>
      )}
    </Box>
  );
}
